import type { ComponentType, ReactNode } from "react";
import {
  MuiCloseIcon,
  MuiErrorOutlineIcon,
  MuiInfoOutlinedIcon,
  MuiReportProblemOutlinedIcon,
  MuiSuccessOutlinedIcon,
} from "@/components/ui/mui-alert-icons";
import { cn } from "@/lib/utils";

// MUI's `Alert` in its **standard** variant — the donor's banner and the body of
// its snackbars — and, since the Integrations panel (#747), its **outlined**
// variant and `AlertTitle`; a shared donor component in the sense the migration
// guidelines mean: the old theme overrides `warning.main` and, of `MuiAlert`
// itself, only `filledSuccess` — a variant nothing here draws. So **MUI's own
// default is the source spec** (`docs/agents/migration-guidelines.md`, "Shared
// donor components port as shared Atlas components"). It lives here, as a
// sibling of the vendored shadcn primitives, rather than as parity classes on
// the one page that needed it first.
//
// The warning colours were **measured off the running donor stack** — the item
// edit page's "changes will impact N specifications" banner at
// `/digitalops/products/:id/edit`. That distinction earned its keep: MUI derives
// the standard variant's ink and ground from `palette[severity].light` rather
// than `main`, and `warning.light` is itself computed from the overridden
// `main`, so the arithmetic is two steps removed from anything written down.
// Having both, the measurement then *validated* the formula — `darken(light,
// 0.6)` and `lighten(light, 0.9)` reproduce the measured pair exactly — which is
// what let the success and error colours below be derived instead of measured;
// their trigger is a completed save, and the donor is read-only to us. All eight
// colours are tokens in `index.css`.

const SEVERITIES = {
  warning: {
    Icon: MuiReportProblemOutlinedIcon,
    surface: "bg-mui-warning-surface",
    border: "border-mui-warning-border",
    ink: "text-mui-warning-ink",
    icon: "text-mui-warning",
  },
  success: {
    Icon: MuiSuccessOutlinedIcon,
    surface: "bg-mui-success-surface",
    border: "border-mui-success-border",
    ink: "text-mui-success-ink",
    icon: "text-mui-success",
  },
  error: {
    Icon: MuiErrorOutlineIcon,
    surface: "bg-mui-error-surface",
    border: "border-mui-error-border",
    ink: "text-mui-error-ink",
    icon: "text-lennar-error",
  },
  // Measured off the details page's draft banner (#694): the donor theme sets
  // `info` to MUI's `blue`, so the standard variant is `blue[300]` darkened and
  // lightened, and the icon `blue[500]`.
  info: {
    Icon: MuiInfoOutlinedIcon,
    surface: "bg-mui-info-surface",
    border: "border-mui-info-border",
    ink: "text-mui-info-ink",
    icon: "text-mui-info",
  },
} as const;

export type MuiAlertSeverity = keyof typeof SEVERITIES;

interface MuiAlertProps {
  /** MUI's `severity`, and the only thing that picks the icon and the three
   * colours. */
  severity: MuiAlertSeverity;
  /**
   * MUI's `variant`, of the two this app has met. `standard` (MUI's default) is
   * the tinted ground; `outlined` is a transparent ground inside a 1px border
   * in the severity's `light`, with the same ink and icon — the Integrations
   * panel's error alerts (#747).
   */
  variant?: "standard" | "outlined";
  /**
   * MUI's `AlertTitle`, drawn above the message inside the message box — the
   * Integrations card's "Last probe failed". A `body1` `<div>` at weight 500,
   * pulled up 2px with `gutterBottom` below (`AlertTitle.js:29-58`), in the
   * alert's ink.
   */
  title?: ReactNode;
  /**
   * The icon's colour over the severity's own — the donor's `'.MuiAlert-icon':
   * { color: 'orange' }` on the past-version banner and the effective-date
   * alert. A colour class; the slot's size and padding stay MUI's.
   */
  iconClassName?: string;
  /**
   * Draws MUI's close action — the `IconButton` it adds to an `Alert` given an
   * `onClose`, not a decoration. The donor's inline banner passes none and so
   * has no X; its snackbar passes one.
   */
  onClose?: () => void;
  /**
   * MUI's `icon`, which replaces the severity's own glyph and nothing else — the
   * Spec list's success snackbar passes `CheckCircleOutlined`
   * (`SpecsListTableIsland.tsx:708`). The slot and its ink stay the
   * severity's; the glyph draws at `SvgIcon`'s own 24px, as a passed MUI icon
   * does.
   */
  icon?: ComponentType<{ className?: string }>;
  /**
   * Classes for MUI's message slot — a donor's `'& .MuiAlert-message'` rule,
   * the community page's "New feature" alert (`width: 100%`, `padding:
   * 0.25rem`). The slot's `min-width` and `overflow` stay MUI's.
   */
  messageClassName?: string;
  /**
   * react-ui's `role` (`alert/alert.tsx`, `role = 'alert'`), which it passes to
   * MUI's root. `status` makes a polite live region instead — the target-cost
   * load panel's endings, which arrive by a poll and must not interrupt.
   */
  role?: "alert" | "status";
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function MuiAlert({
  severity,
  variant = "standard",
  title,
  iconClassName,
  onClose,
  icon: IconOverride,
  messageClassName,
  role = "alert",
  children,
  className,
  "data-testid": testId,
}: MuiAlertProps) {
  const { Icon: SeverityIcon, surface, border, ink, icon } = SEVERITIES[severity];
  const Icon = IconOverride ?? SeverityIcon;

  return (
    <div
      data-slot="mui-alert"
      data-severity={severity}
      data-variant={variant}
      data-testid={testId}
      // `role="alert"`, which is what MUI's own `Alert` renders — for the inline
      // banner a live region that never changes, announced once when the page is
      // read; for a snackbar, the announcement itself. A caller may ask for
      // `status`, as react-ui's own `role` prop allows.
      role={role}
      className={cn(
        // `body2` verbatim: 14px / 1.43, and **no letter-spacing** — `Alert.js:59`
        // spreads the variant whole, but under the donor's Inter theme `body2`
        // has no tracking to spread (the Inter guard, `mui-field-shape.ts`
        // header). The donor's `MuiAlert` override (`theme.ts:377-383`) touches
        // only `filledSuccess`'s colours, so nothing puts one back.
        "flex rounded-[4px] px-4 py-1.5 text-sm leading-[1.43]",
        variant === "outlined" ? cn("border border-solid bg-transparent", border) : surface,
        ink,
        className,
      )}
    >
      {/* MUI's `AlertIcon`: 22px, the severity's `main` itself rather than the
        ink, at 0.9 opacity, with 7px of vertical padding and 12px of clearance —
        which is what centres a 22px glyph against a 20px line of text. */}
      {/* `fontSize: 22` on the *slot*, and the glyph at `1em` — MUI's own
        arrangement, kept so the 22px lives at the one place MUI writes it
        (`mui-alert-icons.tsx`, "Sizing follows the slot"). */}
      <span
        aria-hidden="true"
        className={cn("mr-3 flex py-[7px] text-[22px] opacity-90", icon, iconClassName)}
      >
        {/* A replacing glyph keeps `SvgIcon`'s own `fontSize: 'medium'`
            (1.5rem), which outranks the slot's 22px — only MUI's severity
            glyphs are drawn at `fontSize="inherit"`. */}
        {IconOverride ? <Icon className="text-[1.5rem]" /> : <Icon />}
      </span>
      {/* `AlertMessage`, all three declarations (`Alert.js:119-125`): the 8px
        band that gives the banner its 48px height against a single line,
        `min-width: 0`, and the `overflow: auto` that keeps an unbreakable
        token — an id, a URL, a path — scrolling inside the message box
        instead of painting outside the alert's rounded panel. */}
      <div className={cn("min-w-0 overflow-auto py-2", messageClassName)}>
        {title !== undefined ? (
          // `AlertTitle`: `Typography` at `body1` (1rem / 1.5, no tracking
          // under the Inter theme) with `gutterBottom` (0.35em below), then
          // `fontWeight: 500` and `marginTop: -2` on top. No colour of its
          // own: it takes the alert's ink.
          <div
            data-slot="mui-alert-title"
            className="-mt-0.5 mb-[0.35em] text-base leading-[1.5] font-medium"
          >
            {title}
          </div>
        ) : null}
        {children}
      </div>
      {onClose ? (
        // `AlertAction`: pushed right, top-aligned, 16px of clearance from the
        // message, and pulled 8px back out on the right so the small
        // `IconButton`'s own 5px of padding lands the 20px glyph on the alert's
        // 16px inset rather than beside it.
        <div className="-mr-2 ml-auto flex items-start pt-1 pl-4">
          <button
            type="button"
            // Both, and from the one string. `Alert.js:242-243` spells
            // `aria-label={closeText}` and `title={closeText}` from a single
            // `closeText = 'Close'` default — so the donor's close button
            // raises a native tooltip on hover, not only a name for a screen
            // reader. Atlas stated the name and dropped the tooltip.
            aria-label="Close"
            title="Close"
            onClick={onClose}
            className="flex cursor-pointer rounded-full p-[5px] text-inherit hover:bg-black/4"
          >
            {/* 20px: `Alert` passes `fontSize="small"` to the glyph
                explicitly (`Alert.js:248-249`), which outranks the small
                `IconButton`'s own 18px. */}
            <MuiCloseIcon className="text-[20px]" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
